import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { NewLayoutProps } from './NewLayout.types';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import PagePath from '../../components/PagePath';
import PageTitle from '../../components/PageTitle/PageTitle';
import UserWebInfo from '../../components/UserWebInfo/UserWebInfo';
import { Image } from 'react-native';
import { addBase64Prefix } from '../../services/addBase64Prefix';
import { filter_button, reset_icon } from '../../assets/base64';
import { IS_WEB } from '../../constants/platform';
import { STYLE_VARS } from '../../constants/style-vars';
import { Promotions } from '../../screens/Home/components/Promotions';
import { Footer } from '../../components/Footer';
import { FONTS } from '../../constants';
import { NavigationService } from '../../services/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../services/redux';
import { NewsFilter, setLoadingNews, setNews, setNewsFilter, setTotal } from '../../services/redux/reducers/NewsSlice';
import { NewApi } from '../../services/api';
import { decode } from 'html-entities';

type NewsCategory = {
  id: number;
  name: string;
  slug: string;
  description: string;
  parent: number;
  child?: NewsCategory[];
};

const NewLayout: FC<NewLayoutProps> = ({
  children,
  icon,
  title,
  haveGoBackButton = false,
  havePromotion = true,
  haveTitleBottomLine = true,
}) => {
  const dispatch = useDispatch();
  const { newsFilter } = useSelector((state: RootState) => state.newsReducer);

  const [newsCategories, setNewsCategories] = useState<NewsCategory[]>([]);
  const [filterSelected, setFilterSelected] = useState<NewsCategory[]>([]);

  const getNews = useCallback(
    async (props: NewsFilter) => {
      try {
        dispatch(setLoadingNews(true));
        const res = await NewApi.getNews(props);
        dispatch(setNews(res.data.news));
        dispatch(setTotal(res.data.total));
      } catch (error: any) {
        console.log(error);
      } finally {
        dispatch(setLoadingNews(false));
      }
    }, [dispatch]
  )

  const handleGetNewsCategoriesData = useCallback(async () => {
    try {
      const res = await NewApi.getNewsCategories({});
      const data = res.data;
      let categories: NewsCategory[] = [];
      for (let i = 0; i < data.length; i++) {
        let category: NewsCategory = {
          ...data[i],
          child: [],
        };
        if (category.parent === 0) {
          category.child = data.filter((e) => e.parent === category.id);
          categories.push(category);
        }
      }
      setNewsCategories(categories);
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  }, [setNewsCategories]);

  useEffect(() => {
    const filters = filterSelected.filter(
      (filter) => !filter.child || filter.child.length === 0
    );
    const slugArray = filters.map((obj) => obj.slug);
    const resultString = slugArray.join(',');
    if (resultString !== newsFilter.cats) {
      dispatch(setNews([]));
      getNews({
        ...newsFilter,
        cats: resultString,
        page: 1,
      });
      dispatch(
        setNewsFilter({
          ...newsFilter,
          cats: resultString,
          page: 1,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelected, dispatch, getNews]);

  const handleFilterSelect = useCallback(
    (selected: NewsCategory) => {
      if (filterSelected.includes(selected)) {
        const arr = filterSelected.filter((filter) => filter !== selected);
        setFilterSelected(arr);
      } else {
        const arr = [...filterSelected, selected];
        setFilterSelected(arr);
      }
    },
    [filterSelected]
  );
  const handleFilterReset = useCallback(() => {
    setFilterSelected([]);
    getNews({
      ...newsFilter,
      cats: '',
      page: 1,
    });
    dispatch(
      setNewsFilter({
        ...newsFilter,
        cats: '',
        page: 1,
      })
    );
  }, [getNews, newsFilter, dispatch]);

  useEffect(() => {
    handleGetNewsCategoriesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pathSteps = useMemo(() => {
    return [
      {
        name: 'Member area',
        onPress: () => {
          NavigationService.push('Root', {
            screen: 'HomeTab',
          });
        },
      },
      {
        name: 'News',
      },
      {
        name: title,
      },
    ];
  }, [title]);

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View
        style={{
          maxWidth: 1250,
          width: '90%',
          marginHorizontal: 'auto',
          marginTop: 10,
          minHeight: 500,
        }}
      >
        <PagePath pathSteps={pathSteps} />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginVertical: 30,
          }}
        >
          <View style={{ width: '20%', minWidth: 230, marginRight: 20 }}>
            <UserWebInfo />
            <View style={{ marginTop: 60 }}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  borderBottomWidth: 0.5,
                  borderBottomColor: STYLE_VARS.fccDarkGrey,
                  paddingBottom: 15,
                  marginBottom: 15,
                }}
              >
                <Image
                  source={
                    IS_WEB
                      ? addBase64Prefix(filter_button)
                      : require('../../assets/phone-white.png')
                  }
                  style={{ width: 24, height: 24, marginRight: 8 }}
                />
                <Text
                  style={{
                    fontSize: 16,
                    letterSpacing: 2,
                    fontFamily: FONTS.IowanOldStyleBTRoman,
                    color: STYLE_VARS.textColor,
                    textTransform: 'uppercase',
                  }}
                >
                  Filter Club News
                </Text>
              </View>
              <View style={{ flexDirection: 'column' }}>
                {newsCategories.map((category) => (
                  <TouchableOpacity
                    style={[styles.selectWrapper, { alignSelf: 'flex-start' }]}
                    key={category.id}
                    onPress={() => handleFilterSelect(category)}
                  >
                    <View style={styles.selectedBox}>
                      {filterSelected.includes(category) && (
                        <View
                          style={{
                            width: '100%',
                            height: '100%',
                            borderColor: STYLE_VARS.primaryColor,
                            borderWidth: 5,
                          }}
                        ></View>
                      )}
                    </View>
                    <Text style={styles.selectedText}>{decode(category.name)}</Text>
                  </TouchableOpacity>
                ))}
                {filterSelected.map((selected) => {
                  if (selected.child && selected.child.length > 0) {
                    return (
                      <View key={selected.id}>
                        <View
                          style={{ flexDirection: 'row', marginBottom: 20 }}
                        >
                          <View>
                            <Image
                              source={
                                IS_WEB
                                  ? addBase64Prefix(filter_button)
                                  : require('../../assets/phone-white.png')
                              }
                              style={{ width: 17, height: 17, marginRight: 8 }}
                            />
                          </View>
                          <Text style={styles.selectedText}>
                            ALL {decode(selected.name)}
                          </Text>
                        </View>
                        {selected.child.map((filter) => (
                          <TouchableOpacity
                            style={[
                              styles.selectWrapper,
                              { alignSelf: 'flex-start' },
                            ]}
                            key={filter.id}
                            onPress={() => handleFilterSelect(filter)}
                          >
                            <View style={styles.selectedBox}>
                              {filterSelected.includes(filter) && (
                                <View
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    borderColor: STYLE_VARS.primaryColor,
                                    borderWidth: 5,
                                  }}
                                ></View>
                              )}
                            </View>
                            <Text style={styles.selectedText}>
                              {decode(filter.name)}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    );
                  }
                })}
                <TouchableOpacity
                  style={[styles.selectWrapper, { alignSelf: 'flex-start' }]}
                  onPress={() => handleFilterReset()}
                >
                  <Image
                    source={
                      IS_WEB
                        ? addBase64Prefix(reset_icon)
                        : require('../../assets/reset-icon.png')
                    }
                    style={{
                      width: 17,
                      height: 17,
                      marginRight: 8,
                    }}
                  />
                  <Text style={styles.selectedText}>RESET FILTER</Text>
                </TouchableOpacity>
              </View>
            </View>
            {havePromotion && <Promotions />}
          </View>
          <View style={{ width: '75%' }}>
            <PageTitle
              icon={icon}
              title={title}
              haveGoBackButton={haveGoBackButton}
              haveBottomLine={haveTitleBottomLine}
            />
            {children}
          </View>
        </View>
      </View>
      <Footer />
    </ScrollView>
  );
};

export default NewLayout;

const styles = StyleSheet.create({
  topHeaderLine: {
    width: 70,
    height: 0.5,
    backgroundColor: STYLE_VARS.kccBgGreen,
  },
  topHeaderTitle: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 16,
    color: STYLE_VARS.primaryColor,
    textTransform: 'uppercase',
    marginStart: 8,
    marginEnd: 8,
    letterSpacing: 2,
  },
  bottomHeader: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 25,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
    marginBottom: 2,
    letterSpacing: 3,
  },
  selectWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  selectedText: {
    fontSize: 15,
    fontFamily: FONTS.AvenirMedium,
    color: STYLE_VARS.textColor,
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  selectedBox: {
    width: 17,
    height: 17,
    borderWidth: 0.5,
    borderColor: STYLE_VARS.fccDarkGrey,
    borderRadius: 2,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
